<template>
  <div class="multiselect grow relative text-[1.125rem]">
    <p
      v-if="!noLabel"
      class="mb-[10px]"
    >
      {{ label }}
    </p>
    <div ref="select_container">
      <button
        :disabled="disabled"
        role="combobox"
        :aria-expanded="isDropdownOpen"
        aria-haspopup="listbox"
        class="flex items-center w-full border-[1px] border-[#E2E2E2] p-[12px] rounded-[8px] text-left"
        :class="[triggerClass, disabled ? 'opacity-60 cursor-not-allowed' : 'bg-white']"
        :style="disabled && disabledBgColor ? { backgroundColor: disabledBgColor + ' !important' } : {}"
        @click="toggleDropdown"
      >
        <slot
          name="placeholder"
          :selected="selected"
          :initial-text="placeholder"
        >
          <div class="grow m-0 text-[1rem] text-[#626262] font-medium">
            {{ selectedPlaceholder }}
          </div>
        </slot>
        <i
          class="k-arrow-down-1 text-center cursor-pointer mt-[3px] transition"
        />
      </button>

      <!-- Dropdown options -->
      <div class="relative">
        <div
          v-if="isDropdownOpen"
          ref="options_container"
          :class="{ 'options-container': true, show: isDropdownOpen }"
          role="listbox"
          :aria-labelledby="id"
          aria-multiselectable="true"
          tabindex="-1"
          @keydown.up.prevent="navigateOptions(-1)"
          @keydown.down.prevent="navigateOptions(1)"
          @keydown.esc.prevent="closeDropdown"
        >
          <!-- Search input -->
          <div
            v-if="searchable"
            class="search-wrapper"
          >
            <input
              ref="search_input"
              v-model="searchQuery"
              type="text"
              class="search-input"
              placeholder="Cari..."
              @click.stop
              @keydown.esc.prevent="closeDropdown"
            >
          </div>
          <slot
            v-if="filteredOptions.value.length > 0"
            :id="id"
            :options="filteredOptions"
            :is-selected-all="selectAll"
            :toggleAll="toggleAll"
            :selected="selected"
          >
            <div
              v-if="!searchQuery && !noSelectAll"
              class="checkbox-wrapper"
            >
              <b-form-checkbox
                ref="citemsall"
                v-model="selectAll"
                :aria-describedby="id"
                :aria-controls="id"
                class="custom-checkbox-all"
                tabindex="0"
                @change="toggleAll"
              >
                Semua
              </b-form-checkbox>
            </div>
            <b-form-checkbox-group
              v-model="selected"
              :name="id"
              :aria-describedby="id"
              stacked
            >
              <template>
                <div
                  v-for="(option, index) in filteredOptions.value"
                  :key="index"
                  class="checkbox-wrapper"
                >
                  <b-form-checkbox
                    ref="citems"
                    :value="option"
                  >
                    {{ filteredOptions.text[index] }}
                  </b-form-checkbox>
                </div>
              </template>
            </b-form-checkbox-group>
          </slot>
          <slot
            v-if="(filteredOptions.value.length <= 0 || isError) && !isLoading"
            name="error"
          >
            <p class="text-[12px] text-red-900 m-[8px]">
              Tidak ada data yang ditampilkan.
            </p>
          </slot>

          <!-- Sentinel untuk infinite scroll -->
          <div
            v-show="hasNextPage || isLoading"
            ref="sentinel"
            class="load-more-sentinel"
            aria-hidden="true"
          >
            <b-spinner
              v-if="isLoading"
              small
              variant="secondary"
            />
            <span
              v-if="isLoading"
              class="load-more-text"
            >{{ filteredOptions.value.length > 0 ? 'Memuat lebih banyak...' : 'Memuat data...' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onClickOutside } from '@vueuse/core'

/**
 * MultiSelect component that supports multiple selection, searching,
 * "select all" functionality, and infinite scrolling.
 *
 * @displayName MultiSelect
 */
export default {
  props: {
    value: {
      type: [Array, String],
      default: () => [],
    },
    id: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disabledBgColor: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'Pilih Data',
    },
    placeholder: {
      type: String,
      default: 'test...',
    },
    /**
     * Opsi yang ditampilkan di dalam dropdown.
     * Berisi array `text`, `value`, dan opsional `uncheckOthers` (flag eksklusif per opsi).
     */
    options: {
      type: Object,
      default: () => ({
        text: [],
        value: [],
        uncheckOthers: [],
      }),
      required: true,
    },
    isError: {
      type: Boolean,
      default: false,
    },
    noLabel: {
      type: Boolean,
      default: false,
    },
    triggerClass: {
      type: [String, Array, Object],
      default: '',
    },
    /**
     * Apakah masih ada data berikutnya yang bisa dimuat.
     * Jika `true`, sentinel loader akan ditampilkan di bawah daftar opsi
     * dan event `on-intersect` akan dipancarkan saat sentinel terlihat.
     */
    hasNextPage: {
      type: Boolean,
      default: false,
    },
    /**
     * Status loading data.
     * Jika `true`, spinner loading ditampilkan di sentinel.
     */
    isLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * Aktifkan fitur pencarian/filter opsi di dalam dropdown.
     */
    searchable: {
      type: Boolean,
      default: false,
    },
    /**
     * Sembunyikan opsi "Semua" (Select All) di bagian paling atas dropdown.
     */
    noSelectAll: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDropdownOpen: false,
      selected: [],
      focusedOption: 1,
      cleanupClickOutside: null,
      /** @type {IntersectionObserver|null} */
      observer: null,
      searchQuery: '',
      /** @type {ReturnType<typeof setTimeout>|null} */
      searchDebounceTimer: null,
    }
  },
  computed: {
    /**
     * Filters the options based on the current search query.
     * Returns the original options if search is disabled or query is empty.
     *
     * @returns {{ value: Array, text: Array }} Filtered options object.
     */
    filteredOptions() {
      if (!this.searchable || !this.searchQuery.trim()) {
        return this.options
      }
      const query = this.searchQuery.toLowerCase()
      const result = { value: [], text: [], uncheckOthers: [] }
      const hasUncheck = Array.isArray(this.options.uncheckOthers)
      this.options.text.forEach((text, i) => {
        if (String(text).toLowerCase().includes(query)) {
          result.text.push(text)
          result.value.push(this.options.value[i])
          if (hasUncheck) {
            result.uncheckOthers.push(this.options.uncheckOthers[i])
          }
        }
      })
      return result
    },
    /**
     * Computed property that manages the "Select All" checkbox state.
     * Automatically updates based on whether all filtered options are currently selected.
     */
    selectAll: {
      get() {
        const selectableOptions = this.filteredOptions.value.filter(val => !this.isUncheckOthersOption(val))
        return (
          selectableOptions.length > 0
          && selectableOptions.every(val => this.selected.includes(val))
        )
      },
      set(value) {
        if (value) {
          // Tambahkan semua opsi non-eksklusif yang terfilter
          const selectableOptions = this.filteredOptions.value.filter(val => !this.isUncheckOthersOption(val))
          const newSelected = new Set([...this.selected.filter(val => !this.isUncheckOthersOption(val)), ...selectableOptions])
          this.selected = Array.from(newSelected)
        } else {
          // Hanya hapus opsi yang sedang tampil di filter dari daftar pilihan
          this.selected = this.selected.filter(val => !this.filteredOptions.value.includes(val))
        }
      },
    },
    /**
     * Generates the text to display in the dropdown trigger button.
     * Shows the number of selected items or the default placeholder.
     *
     * @returns {string} The placeholder text.
     */
    selectedPlaceholder() {
      return this.selected.length > 0
        ? `${this.selected.length} dipilih`
        : this.placeholder
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        let arr = []
        if (Array.isArray(newVal)) {
          arr = newVal
        } else if (typeof newVal === 'string' && newVal) {
          arr = newVal.split(',').map(s => s.trim())
        } else if (newVal) {
          arr = [newVal]
        }

        const isSame = arr.length === this.selected.length && arr.every((v, i) => v === this.selected[i])
        if (!isSame) {
          this.selected = [...arr]
        }
      },
    },
    selected: {
      handler(newValue, oldValue = []) {
        this.handleUncheckOthers(newValue, oldValue)
        this.$emit('input', this.selected)
      },
    },
    /**
     * Emit event `on-search` dengan debounce 300ms saat searchQuery berubah.
     * Hanya aktif jika prop `searchable` bernilai true.
     */
    searchQuery(newQuery) {
      if (!this.searchable) return
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.$emit('on-search', newQuery.trim())
      }, 300)
    },
    focusedOption(newValue) {
      if (this.options.value.length > 0) {
        this.$nextTick(() => {
          if (newValue === 0) {
            this.$refs.citemsall.focus()
          } else {
            this.$refs.citems[newValue - 1].focus()
          }
        })
      }
    },
    /**
     * Re-observe sentinel setiap kali opsi berubah (misal: data baru dimuat).
     * Hanya dijalankan saat dropdown terbuka agar tidak observe elemen yang
     * tidak ada di DOM.
     */
    options: {
      handler() {
        if (!this.isDropdownOpen) return
        this.$nextTick(() => {
          this.observeSentinel()
        })
      },
      deep: false,
    },
    /**
     * Re-observe sentinel saat hasNextPage berubah menjadi true
     * (misalnya setelah reset data).
     */
    hasNextPage(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.observeSentinel()
        })
      }
    },
  },
  mounted() {
    this.cleanupClickOutside = onClickOutside(this.$refs.select_container, () => {
      if (this.isDropdownOpen) this.isDropdownOpen = false
    })
    // Observer dibuat ulang saat dropdown dibuka agar bisa menggunakan
    // options_container sebagai root (scroll container yang akurat).
    // Lihat toggleDropdown().
  },
  beforeDestroy() {
    if (this.cleanupClickOutside) {
      this.cleanupClickOutside()
    }
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  },
  methods: {
    /**
     * Toggles the dropdown menu's visibility.
     * When opened, it resets the search query, manages focus, and sets up the IntersectionObserver for infinite scrolling.
     */
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen
      if (this.isDropdownOpen) {
        this.searchQuery = ''
        if (this.searchable) {
          this.$nextTick(() => {
            if (this.$refs.search_input) this.$refs.search_input.focus()
          })
        } else if (this.options.value.length > 0) {
          this.$nextTick(() => {
            if (this.$refs.citems && this.$refs.citems[0]) this.$refs.citems[0].focus()
          })
        }
        // Buat observer baru dengan root = scroll container dropdown
        // agar intersection detection akurat (bukan viewport)
        this.$nextTick(() => {
          if (this.observer) this.observer.disconnect()
          this.observer = new IntersectionObserver(this.handleIntersect, {
            root: this.$refs.options_container,
            threshold: 0.1,
          })
          this.observeSentinel()
        })

        return
      }
      // Disconnect saat dropdown ditutup
      if (this.observer) {
        this.observer.disconnect()
      }
    },
    /**
     * Closes the dropdown menu and disconnects the IntersectionObserver.
     */
    closeDropdown() {
      this.isDropdownOpen = false
      if (this.observer) {
        this.observer.disconnect()
      }
    },
    /**
     * Selects or deselects all currently filtered options (excluding uncheckOthers options).
     *
     * @param {boolean} allSelected - Indicates whether to select (true) or deselect (false) all options.
     */
    toggleAll(allSelected) {
      if (allSelected) {
        const selectableOptions = this.options.value.filter(val => !this.isUncheckOthersOption(val))
        this.selected = selectableOptions
      } else {
        this.selected = []
      }
    },
    /**
     * Memeriksa apakah suatu nilai opsi adalah opsi eksklusif (uncheckOthers).
     *
     * @param {*} val - Nilai opsi yang diperiksa
     * @param {number} [index] - Indeks opsi
     * @returns {boolean}
     */
    isUncheckOthersOption(val, index) {
      if (Array.isArray(this.options?.uncheckOthers)) {
        let idx = typeof index === 'number' && index >= 0 ? index : -1
        if (idx === -1 && Array.isArray(this.options?.value)) {
          idx = this.options.value.indexOf(val)
        }

        if (idx !== -1) {
          return Boolean(this.options.uncheckOthers[idx])
        }
      }

      return false
    },
    /**
     * Memastikan mutual exclusion antara opsi eksklusif (uncheckOthers) dan opsi biasa.
     *
     * @param {Array} newValue - Daftar nilai terpilih baru
     * @param {Array} oldValue - Daftar nilai terpilih sebelumnya
     */
    handleUncheckOthers(newValue, oldValue = []) {
      if (!Array.isArray(newValue) || newValue.length <= 1) return

      const oldSet = new Set(Array.isArray(oldValue) ? oldValue : [])
      const newlyAddedExclusive = []
      let hasNewlyAddedNonExclusive = false

      for (let i = 0; i < newValue.length; i += 1) {
        const val = newValue[i]
        if (!oldSet.has(val)) {
          if (this.isUncheckOthersOption(val)) {
            newlyAddedExclusive.push(val)
          } else {
            hasNewlyAddedNonExclusive = true
          }
        }
      }

      let updated = null

      if (newlyAddedExclusive.length > 0) {
        const exclusiveSet = new Set(newlyAddedExclusive)
        updated = newValue.filter(val => exclusiveSet.has(val))
      } else if (hasNewlyAddedNonExclusive) {
        updated = newValue.filter(val => !this.isUncheckOthersOption(val))
      }

      if (updated && (updated.length !== newValue.length || updated.some((v, i) => v !== newValue[i]))) {
        this.selected = updated
      }
    },
    /**
     * Handles keyboard navigation within the dropdown menu (Up/Down arrow keys).
     *
     * @param {number} direction - The direction to move focus (-1 for up, 1 for down).
     */
    navigateOptions(direction) {
      const optionsLength = this.options.value.length + 1
      if (optionsLength === 0) return

      this.focusedOption = (this.focusedOption + direction + optionsLength) % optionsLength
    },
    /**
     * Mulai mengamati elemen sentinel jika tersedia.
     * Dipanggil setiap kali dropdown dibuka atau opsi berubah.
     */
    observeSentinel() {
      if (!this.observer || !this.$refs.sentinel) return
      this.observer.disconnect()
      this.observer.observe(this.$refs.sentinel)
    },
    /**
     * Callback IntersectionObserver.
     * Memancarkan event `on-intersect` ke parent saat sentinel terlihat
     * dan masih ada data berikutnya (`hasNextPage === true`).
     *
     * @param {IntersectionObserverEntry[]} entries
     */
    handleIntersect(entries) {
      const entry = entries[0]
      if (entry.isIntersecting && this.hasNextPage && !this.isLoading) {
        this.$emit('on-intersect')
      }
    },
  },
}
</script>

<style scoped>
.options-container {
  position: absolute;
  max-height: 240px;
  overflow: auto;
  top: 5px;
  left: 0;
  right: 0;
  background-color: #ffffff;
  z-index: 10;
  border-radius: 16px;
  border: 1px solid #e2e2e2;
  padding: 8px;
}
.search-wrapper {
  padding: 4px 4px 8px;
  position: sticky;
  top: 0;
  z-index: 10;
}
.dark-layout .search-wrapper {
  background-color: #283046;
}
.search-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  background-color: #fff;
  color: #333;
}
.dark-layout .search-input {
  background-color: #3b4253;
  border-color: #3b4253;
  color: #fff;
}
.search-input:focus {
  border-color: #7367f0;
}
.dark-layout .options-container {
  background-color: #283046;
  border-color: #3b4253;
  color: #ffffff;
}
.show {
  display: block;
  animation: slide-down 0.2s ease-out forwards;
}
.multiselect:has(.custom-control-input:focus) .k-arrow-down-1 {
  transform: rotate(180deg);
  margin-top: 0;
  margin-bottom: 3px;
}
.options-container /deep/ .checkbox-wrapper {
  padding: 8px;
  border-radius: 8px;
  transition: 0.1s all ease-in-out;
}
.options-container /deep/ .checkbox-wrapper:hover,
.options-container /deep/ .checkbox-wrapper:has(.custom-control-input:focus) {
  background-color: #eeeeee;
}
.dark-layout .options-container /deep/ .checkbox-wrapper:hover,
.dark-layout .options-container /deep/ .checkbox-wrapper:has(.custom-control-input:focus) {
  background-color: #3b4253;
}
.options-container /deep/ .checkbox-wrapper label {
  width: 100%;
}
.options-container .custom-checkbox-all {
  margin: 0;
}
.load-more-sentinel {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 8px;
  min-height: 40px;
}
.load-more-text {
  font-size: 0.8rem;
  color: #626262;
}
.dark-layout .load-more-text {
  color: #b4b7bd;
}
@keyframes slide-down {
  0% {
    clip-path: inset(0 0 100% 0);
  }
  100% {
    clip-path: inset(0);
  }
}
</style>
